import { useNavigate } from "react-router-dom";
import { ExternalLink, CalendarRange, Star, TrendingUp, Eye, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import type { EventRow, CarrRelevanceTier } from "@/hooks/useEvents";
import { format } from "date-fns";

const TIER_CONFIG: Record<string, { color: string; icon: typeof Star; label: string }> = {
  "Tier 1: Must-Attend": { color: "bg-red-100 text-red-800 border-red-200", icon: Star, label: "Must-Attend" },
  "Tier 2: High-Value": { color: "bg-amber-100 text-amber-800 border-amber-200", icon: TrendingUp, label: "High-Value" },
  "Tier 3: Monitor": { color: "bg-slate-100 text-slate-700 border-slate-200", icon: Eye, label: "Monitor" },
};

function TierBadge({ tier }: { tier: CarrRelevanceTier | null }) {
  if (!tier) return null;
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs gap-1`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

function formatDateRange(start: string, end: string | null): string {
  const s = format(new Date(start + "T00:00:00"), "MMM d, yyyy");
  if (!end || end === start) return s;
  const e = format(new Date(end + "T00:00:00"), "MMM d, yyyy");
  return `${s} – ${e}`;
}

interface Props {
  events: EventRow[];
  isLoading: boolean;
}

export default function EventIntelligenceTable({ events, isLoading }: Props) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <CalendarRange className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground">No events found</h3>
        <p className="text-muted-foreground mt-1 max-w-md">Adjust your filters or add a new event.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Event Name</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Dates</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Territory</TableHead>
          <TableHead className="text-center">Accounts</TableHead>
          <TableHead className="text-center">Competitors</TableHead>
          <TableHead className="w-10"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event) => (
          <TableRow
            key={event.id}
            className="cursor-pointer"
            onClick={() => navigate(`/events/${event.id}`)}
          >
            <TableCell>
              <div className="space-y-1">
                <span className="font-medium text-foreground line-clamp-1">{event.event_name}</span>
                {event.focus_areas && event.focus_areas.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {event.focus_areas.slice(0, 2).map((f, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] font-normal">{f}</Badge>
                    ))}
                    {event.focus_areas.length > 2 && (
                      <span className="text-[10px] text-muted-foreground">+{event.focus_areas.length - 2}</span>
                    )}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell><TierBadge tier={event.carr_relevance_tier} /></TableCell>
            <TableCell>
              {event.carr_event_category && (
                <Badge variant="secondary" className="text-xs">{event.carr_event_category}</Badge>
              )}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
              {formatDateRange(event.start_date, event.end_date)}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">{event.location || "—"}</TableCell>
            <TableCell>
              {event.territory && <Badge variant="outline" className="text-[10px]">{event.territory}</Badge>}
            </TableCell>
            <TableCell className="text-center text-sm text-muted-foreground">
              {event.account_count > 0 ? event.account_count : "—"}
            </TableCell>
            <TableCell className="text-center">
              {event.competitor_presence_likely && (
                <Shield className="h-4 w-4 text-orange-600 mx-auto" />
              )}
            </TableCell>
            <TableCell>
              {event.website_url && (
                <a
                  href={event.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
